import { createRootRoute, Link, Outlet, useRouterState } from '@tanstack/react-router'
import { useState, useRef, useEffect } from 'react'
import { Toaster } from 'sonner'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { NotFoundPage } from '@/components/NotFoundPage'
import { OfflineBanner } from '@/components/OfflineBanner'
import { useDarkMode } from '@/hooks/useDarkMode'
import { useDetectedCountry } from '@/hooks/useDetectedCountry'
import {
  ChevronDownIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  UserIcon,
  MenuIcon,
  XIcon,
  SunIcon,
  MoonIcon,
} from 'lucide-react'

/** Derive a display name and two-letter initials from the user object.
 *  Prefers first_name + last_name for both; falls back to name, then email. */
function userDisplay(user: { first_name?: string; last_name?: string; name?: string; email?: string; pseudonym?: string } | null) {
  const first = user?.first_name?.trim()
  const last = user?.last_name?.trim()
  const name = user?.name?.trim()
  const email = user?.email?.trim()
  const pseudonym = user?.pseudonym?.trim()

  const displayName = (first && last) ? `${first} ${last}`
    : name || first || last || pseudonym || email || 'Unknown'

  const initials = (first && last) ? `${first.charAt(0)}${last.charAt(0)}`.toUpperCase()
    : (first || last || name || pseudonym || email || '?').charAt(0).toUpperCase()

  return { displayName, initials }
}

function UserMenu() {
  const { user, logout } = useAuth()
  const [open, setOpen] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const { displayName, initials } = userDisplay(user)

  // Close on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClick)
    }
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      await logout()
      // Hard reload to clear all in-memory state, HTTP cache, and module singletons.
      // Client-side navigation (navigate / Link) keeps stale state in memory.
      window.location.href = '/login'
    } catch {
      // If logout itself fails, still clear state and redirect.
      window.location.href = '/login'
    }
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors hover:bg-muted"
        aria-expanded={open}
        aria-haspopup="true"
      >
        {/* Avatar */}
        <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
          {initials}
        </div>
        <span className="hidden sm:inline max-w-[120px] truncate">{displayName}</span>
        <ChevronDownIcon
          className={`size-3.5 shrink-0 text-muted-foreground transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-56 rounded-lg border border-border bg-card p-1 shadow-lg animate-in fade-in zoom-in-95 origin-top-right">
          {/* User info */}
          <div className="flex items-center gap-3 px-3 py-2.5">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{displayName}</p>
              <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground capitalize">
                {user?.role}
              </span>
            </div>
          </div>

          <div className="my-1 border-t border-border" />

          {/* Dashboard link */}
          <Link
            to="/dashboard"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <LayoutDashboardIcon className="size-4" />
            Dashboard
          </Link>

          {/* Profile link */}
          <Link
            to="/dashboard/profile"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <UserIcon className="size-4" />
            Profile
          </Link>

          {/* Logout */}
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <LogOutIcon className="size-4" />
            {loggingOut ? 'Logging out…' : 'Log out'}
          </button>
        </div>
      )}
    </div>
  )
}

function ThemeToggle() {
  const { isDark, setTheme } = useDarkMode()
  return (
    <button
      type="button"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className="flex size-10 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      {isDark ? <SunIcon className="size-4" /> : <MoonIcon className="size-4" />}
    </button>
  )
}

function Header() {
  const { isAuthenticated } = useAuth()
  const routerState = useRouterState()
  const isLanding = ['/', '/login', '/signup', '/pricing', '/privacy', '/terms', '/dpa', '/cookie-policy', '/contact'].includes(
    routerState.location.pathname,
  )
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const landingNavItems = [
    { href: '#how-it-works', label: 'How it Works' },
    { href: '#features', label: 'Features' },
    { href: '/pricing', label: 'Pricing', isRoute: true },
    { href: '#faq', label: 'FAQ' },
  ]

  const handleNavClick = () => setMobileNavOpen(false)

  if (isLanding) {
    return (
      <>
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0B1120]/80 backdrop-blur-lg border-b border-white/10">
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="flex size-9 items-center justify-center rounded-xl bg-[#00E5A0] text-[#0B1120] text-sm font-bold">
                PP
              </div>
              <span className="text-lg font-semibold tracking-tight text-white">PeerPass</span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-8">
              {landingNavItems.map((item) =>
                item.isRoute ? (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    key={item.href}
                    href={item.href}
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {item.label}
                  </a>
                ),
              )}
            </nav>

            <div className="hidden md:flex items-center gap-3">
              <ThemeToggle />
              <Link to="/login">
                <Button variant="ghost" className="text-white/70 hover:text-white hover:bg-white/10">
                  Log in
                </Button>
              </Link>
              <a href="#book-demo">
                <Button className="bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold shadow-lg shadow-[#00E5A0]/20">
                  Book a Demo
                </Button>
              </a>
            </div>

            {/* Mobile hamburger */}
            <button
              type="button"
              aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileNavOpen((v) => !v)}
              className="flex md:hidden size-10 items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              {mobileNavOpen ? <XIcon className="size-5" /> : <MenuIcon className="size-5" />}
            </button>
          </div>
        </header>

        {/* Mobile nav drawer */}
        {mobileNavOpen && (
          <>
            <button
              type="button"
              aria-label="Close menu"
              tabIndex={-1}
              className="fixed inset-0 z-40 bg-black/60 md:hidden"
              onClick={() => setMobileNavOpen(false)}
            />
            <nav className="fixed top-16 left-0 right-0 z-40 flex flex-col gap-1 border-b border-white/10 bg-[#0B1120] p-6 md:hidden animate-in slide-in-from-top-2 fade-in duration-200">
              {landingNavItems.map((item) =>
                item.isRoute ? (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={handleNavClick}
                    className="rounded-lg px-3 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={handleNavClick}
                    className="rounded-lg px-3 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    {item.label}
                  </a>
                ),
              )}
              <div className="my-2 border-t border-white/10" />
              <Link to="/login" onClick={handleNavClick}>
                <Button variant="ghost" className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10">
                  Log in
                </Button>
              </Link>
              <a href="#book-demo" onClick={handleNavClick}>
                <Button className="w-full bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold">
                  Book a Demo
                </Button>
              </a>
            </nav>
          </>
        )}
      </>
    )
  }

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-sm supports-backdrop-filter:bg-background/60">
      <div className="flex h-14 items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-2 font-semibold text-lg tracking-tight hover:opacity-80 transition-opacity">
          <div className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground text-sm font-bold">
            PP
          </div>
          <span>PeerPass</span>
        </Link>
        <nav className="flex items-center gap-3">
          <ThemeToggle />
          {isAuthenticated ? (
            <UserMenu />
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost">Log in</Button>
              </Link>
              <Link to="/signup">
                <Button>Sign up</Button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  )
}

/**
 * Warm the country-detection cache at app boot (renders nothing).
 *
 * useDetectedCountry() writes a successful detection to sessionStorage, and
 * the /pricing and /signup hooks initialize from that cache synchronously.
 * Mounting the hook here — before any navigation — means the first visit to
 * /pricing renders the detected country/plans on first paint instead of
 * showing the EUR-default pulse until the API round-trip completes.
 */
export function CountryDetectionPrefetcher() {
  useDetectedCountry()
  return null
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
})

function RootComponent() {
  return (
    <ErrorBoundary>
      <div className="flex min-h-svh flex-col">
        <CountryDetectionPrefetcher />
        <OfflineBanner />
        <Header />
        <main className="flex-1">
          <Outlet />
        </main>
        <Toaster position="top-center" richColors />
      </div>
    </ErrorBoundary>
  )
}
